import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, IconButton, Button } from '@mui/material';
import { FaUsers, FaCalendarCheck, FaUserClock, FaPlus, FaHandHoldingUsd } from 'react-icons/fa';
import { MdEvent, MdPersonAdd, MdSettings, MdDashboard } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL } from '../../App.js';
import ClubhouseBookingModal from './components/ClubhouseBookingModal';
import AdminBookingModal from './components/AdminBookingModal';
import { useNavigate } from 'react-router-dom';
import { MdCalendarToday } from 'react-icons/md';

const StatCard = ({ title, value, icon, color }) => (
  <Paper 
    elevation={0} 
    sx={{ 
      p: 3, 
      height: '100%', 
      bgcolor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      border: '1px solid rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 12px 20px -10px rgba(0,0,0,0.1)',
        bgcolor: 'rgba(255, 255, 255, 1)',
        '& .icon-bg': {
          transform: 'scale(1.1) rotate(-5deg)',
        }
      }
    }}
  >
    <Box>
      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {value}
      </Typography>
    </Box>
    <Box className="icon-bg" sx={{ 
      color: `${color}.main`, 
      fontSize: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: `${color === 'primary' ? 'rgba(211, 47, 47, 0.1)' : color === 'success' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(237, 108, 2, 0.1)'}`,
      p: 1.5,
      borderRadius: '10px',
      transition: 'transform 0.3s ease'
    }}>
      {icon}
    </Box>
  </Paper>
);

const QuickAction = ({ icon, label, onClick, color = 'primary' }) => (
  <Button
    variant="text"
    onClick={onClick}
    sx={{
      flexDirection: 'column',
      gap: 1,
      minWidth: { xs: '80px', sm: '100px' },
      p: 2,
      borderRadius: '12px',
      color: 'text.secondary',
      '&:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.5)',
        color: `${color}.main`,
        '& .icon-wrap': {
          bgcolor: `${color}.main`,
          color: 'white',
          transform: 'translateY(-2px)'
        }
      }
    }}
  >
    <Box className="icon-wrap" sx={{
      width: 44,
      height: 44,
      borderRadius: '12px',
      bgcolor: 'rgba(0,0,0,0.04)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 22,
      transition: 'all 0.2s'
    }}>
      {icon}
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
      {label}
    </Typography>
  </Button>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAdminBooking, setShowAdminBooking] = useState(false);
  const [players, setPlayers] = useState([]);

  const fetchPlayers = () => {
    axios.get(`${API_BASE_URL}/api/players`)
      .then(res => setPlayers(res.data))
      .catch(err => console.error('Error fetching players:', err));
  };

  const fetchStats = () => {
    axios.get(`${API_BASE_URL}/api/stats/dashboard`)
      .then(response => {
        setStats(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching dashboard stats:', err);
        setError('Fehler beim Laden der Statistiken');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchStats();
    fetchPlayers();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, md: 3 }, 
          mb: 4,
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 1, bgcolor: 'primary.main', borderRadius: '10px', color: 'white', display: 'flex' }}>
                <MdDashboard size={24} />
            </Box>
            <Typography variant="h5" component="h1" color="primary">
            Admin Dashboard
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <QuickAction icon={<MdCalendarToday />} label="Neue Buchung" onClick={() => setShowAdminBooking(true)} color="success" />
            <QuickAction icon={<MdEvent />} label="Termine" onClick={() => navigate('/admin/events')} />
            <QuickAction icon={<MdPersonAdd />} label="Mitglied" onClick={() => navigate('/admin/members')} />
            <QuickAction icon={<FaHandHoldingUsd />} label="Gastgebühren" onClick={() => navigate('/admin/guests')} />
            <QuickAction icon={<MdSettings />} label="Verein" onClick={() => navigate('/admin/club')} />
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Aktive Mitglieder"
            value={stats?.activeMembers || 0}
            icon={<FaUsers />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Kommende Buchungen"
            value={stats?.upcomingBookings || 0}
            icon={<FaCalendarCheck />}
            color="success"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <StatCard
            title="Unbezahlte Gaststunden"
            value={stats?.unpaidGuests || 0}
            icon={<FaUserClock />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Widgets Row */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6} lg={4}>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Clubheim
                    </Typography>
                    <IconButton 
                        size="small" 
                        variant="contained"
                        sx={{ bgcolor: 'rgba(0,0,0,0.05)', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}
                        onClick={() => setShowModal(true)}
                    >
                        <FaPlus size={14} />
                    </IconButton>
                </Box>
                
                {stats?.nextClubhouseBookings && stats.nextClubhouseBookings.length > 0 ? (
                    <Box>
                        {stats.nextClubhouseBookings.slice(0, 3).map((booking, index) => (
                            <Box key={booking._id} sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                py: 1.5, 
                                borderBottom: index < 2 ? '1px solid #eee' : 'none' 
                            }}>
                                {/* Row 1: Date and Name */}
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                                    <Box sx={{ minWidth: '85px' }}>
                                        <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} className="fs-7">
                                            {new Date(booking.date).toLocaleDateString('de-DE')}
                                        </Typography>
                                    </Box>
                                    <Typography noWrap className="fs-7" sx={{ fontWeight: 500, flex: 1 }}>
                                        {booking.rentedBy && booking.rentedBy.firstName ? `${booking.rentedBy.firstName} ${booking.rentedBy.lastName}` : 'Unbekannt'}
                                    </Typography>
                                </Box>

                                {/* Row 2: Time */}
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mt: 0.2 }}>
                                    <Box sx={{ minWidth: '85px' }}>
                                        {booking.startTime && (
                                            <Typography color="text.secondary" sx={{ fontSize: '0.85em', whiteSpace: 'nowrap' }} className="fs-7">
                                                ab {booking.startTime} Uhr
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* Row 3: Note (Full width, wrapping) */}
                                {booking.notes && (
                                    <Box sx={{ pr: 1, mt: 0.2 }}>
                                        <Typography 
                                            color="text.secondary" 
                                            sx={{ 
                                                fontSize: '0.75rem',
                                                fontStyle: 'italic',
                                                lineHeight: 1.3,
                                                wordBreak: 'break-word',
                                                display: 'block'
                                            }} 
                                            className="fs-7"
                                        >
                                            "{booking.notes}"
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                        {stats.nextClubhouseBookings.length > 3 && (
                             <Typography variant="caption" color="primary" sx={{ display: 'block', textAlign: 'center', mt: 1, cursor: 'pointer' }} onClick={() => window.location.href='/admin/clubhouse'}>
                                + {stats.nextClubhouseBookings.length - 3} weitere
                             </Typography>
                        )}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Keine bevorstehenden Reservierungen.
                    </Typography>
                )}
            </Paper>
        </Grid>
        
      </Grid>

      <ClubhouseBookingModal
          show={showModal}
          onHide={() => setShowModal(false)}
          booking={null}
          refresh={fetchStats}
      />

      <AdminBookingModal
          open={showAdminBooking}
          onClose={(success) => {
              setShowAdminBooking(false);
              if (success) fetchStats();
          } }
          players={players}
      />
    </Box>
  );
};

export default Dashboard;